<script setup>
import { providePageGuide } from "@/composables/usePageGuide"
providePageGuide()

import "@styles/admin-ios-controls.css"

const businessStore = useBusinessStore()
const { isLoading } = useApiLoading()
const showGlobalLoading = ref(false)
let loadingDelay = null

useHead({ bodyAttrs: { class: "business-panel-page" } })

const hasVisibleLocalButtonLoading = () =>
  import.meta.client &&
  Boolean(
    document.querySelector(
      ".business-layout .v-btn--loading, .business-layout button[aria-busy='true']"
    )
  )

watch(isLoading, value => {
  if (loadingDelay) clearTimeout(loadingDelay)

  if (value) {
    loadingDelay = setTimeout(() => {
      showGlobalLoading.value = !hasVisibleLocalButtonLoading()
    }, 120)
  } else {
    showGlobalLoading.value = false
  }
})

onBeforeUnmount(() => loadingDelay && clearTimeout(loadingDelay))
onMounted(() => businessStore.loadCompanies())
</script>

<template>
  <div class="business-layout" dir="rtl">
    <BusinessSidebar />
    <div class="business-layout__main">
      <BusinessHeader />
      <main class="business-layout__content"><slot /></main>
    </div>
    <BusinessMobileNav />
    <Transition name="business-loading-fade">
      <div
        v-if="showGlobalLoading"
        class="business-global-loading"
        role="status"
        aria-live="polite"
        aria-label="در حال بارگذاری"
      >
        <div class="business-global-loading__card">
          <VProgressCircular indeterminate color="primary" size="38" width="4" />
          <div>
            <strong>در حال بارگذاری</strong>
            <span>لطفاً چند لحظه صبر کنید...</span>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<style lang="scss">
.business-layout { display: flex; width:100%; max-width:100%; min-width:0; min-height: 100vh; overflow-x:clip; color: #332e45; background: #f7f6fa; font-size:14px; }
.business-layout * { box-sizing: border-box; }
.business-layout__main { width: calc(100% - 266px); max-width:100%; min-width: 0; }
.business-layout__content { width: min(100%, 1180px); max-width:100%; min-width:0; margin: 0 auto; padding: 22px 24px 54px; overflow-x:clip; }
.business-layout__content > .v-card { overflow: visible; border: 1px solid rgba(47,42,69,.07) !important; border-radius: 20px !important; background: #fff !important; box-shadow: 0 8px 28px rgba(39,33,68,.055) !important; }
.business-layout__content .v-btn { letter-spacing: 0; text-transform: none; }
.business-layout__content .v-field { border-radius: 12px !important; }
.business-layout__content .v-table,.business-layout__content .v-data-table { overflow: hidden; border: 1px solid #eeebf3; border-radius: 15px; }
.business-layout__content h1,.business-layout__content h2,.business-layout__content h3 { color: #302a43; }
.business-layout__content .v-card[class*="mt-"] { margin-top: 0 !important; }
.business-layout__content .v-card[class*="pa-"] { padding: clamp(14px,2.2vw,25px) !important; }
.business-layout__content .v-label { font-size: 13px; }
.business-layout__content .v-field__input,
.business-layout__content .v-select__selection-text,
.business-layout__content .v-autocomplete__selection-text { font-size: 13px !important; }
.business-layout__content .v-btn { min-height: 42px; border-radius: 11px; font-size: 12px; font-weight: 700; }
.business-layout__content .v-data-table-header__content { font-size: 12px; font-weight: 800; }
.business-layout__content .v-data-table__td { font-size: 12px; }
.business-layout__content .v-card-title { font-size: 16px; }
.business-layout__content .v-card-subtitle { font-size: 12px; }
.business-layout__content :where(p, .text-body-2) { font-size: 12.5px !important; }
.business-layout__content :where(small, .text-caption) { font-size: 10.5px !important; }
.business-layout__content h1 { font-size: clamp(20px,2vw,25px); }
.business-layout__content h2 { font-size: clamp(17px,1.6vw,21px); }
.business-layout__content h3 { font-size: clamp(14px,1.3vw,17px); }
.business-sidebar__brand strong { font-size:17px !important; }
.business-sidebar__brand small { font-size:10.5px !important; }
.business-sidebar nav strong { font-size:13px !important; }
.business-sidebar nav small { font-size:10px !important; }
.business-sidebar__exit { font-size:12px !important; }
.business-header h1 { font-size:20px !important; }
.business-header p { font-size:11px !important; }
.business-header__profile { font-size:12px !important; }
.business-context .v-field__input { font-size:13px !important; }
.business-mobile-nav a { font-size:10px !important; }
.business-layout__content .v-dialog > .v-overlay__content > .v-card { border-radius: 20px !important; }
.business-layout__content .text-center > h3:first-child { margin-bottom: 18px !important; font-size: 16px; }
.business-layout__content .information-settings-parent,.business-layout__content .appointment-settings-parent { padding: 0 !important; margin-top: 0 !important; }
.business-global-loading{position:fixed;z-index:12000;inset:0;display:grid;place-items:center;padding:16px;background:rgba(247,246,250,.6);backdrop-filter:blur(2px)}.business-global-loading__card{display:flex;min-width:230px;align-items:center;gap:13px;padding:16px 18px;border:1px solid rgba(var(--v-theme-primary),.13);border-radius:17px;background:#fff;box-shadow:0 18px 55px rgba(42,34,78,.16)}.business-global-loading__card>div{display:grid;gap:3px}.business-global-loading__card strong{color:#393247;font-size:12px}.business-global-loading__card span{color:#8d8698;font-size:9px}.business-loading-fade-enter-active,.business-loading-fade-leave-active{transition:opacity .18s ease}.business-loading-fade-enter-from,.business-loading-fade-leave-to{opacity:0}
@media (max-width: 959px) { .business-layout__main { width: 100%; min-width:0; } .business-layout__content { padding: 14px 12px 90px; } }
@media (max-width: 599px) { .business-layout { font-size:13px; } .business-layout__content { width:100%; padding-inline: 8px; } .business-layout__content > .v-card { border-radius: 17px !important; } .business-layout__content .v-data-table { overflow-x: auto; } .business-layout__content .v-card[class*="pa-"] { padding: 12px !important; } .business-layout__content .v-label,.business-layout__content .v-field__input,.business-layout__content .v-select__selection-text,.business-layout__content .v-autocomplete__selection-text { font-size:13px !important; } .business-layout__content .v-btn { font-size:11.5px; } .business-header h1 { font-size:17px !important; } }
</style>
